import React from 'react';
import './CalculusOfMeaning.css';

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

function CalculusOfProgress() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Will &middot; Struggle &middot; The Precision of Growth</span>
          <h1>The Calculus of Progress</h1>
          <p className="calculus-hero-sub">The Precision of Progress is cultivated by a leader who understands the consequence of chemistry.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <div className="calculus-content">

          {/* The Rocks */}
          <section className="calc-section reveal">
            <h2>The Rocks</h2>
            <p className="calc-lead">
              Two girls. Eight and seven. Walking two and a half miles home from school
              because no one walked with them. Six older brothers, none of whom showed
              up at the door when the bell rang. Just the two of them, every day, on a
              road that felt longer when someone was throwing things at you.
            </p>
            <p>
              The bully waited for them. Every afternoon. Rocks and names. The rocks
              left marks on skin. The names left marks somewhere deeper, in the place
              where a child is still deciding whether the world is safe or not.
            </p>
            <p>
              They could have run. They could have cried. They could have begged their
              brothers to walk with them. Every one of those responses would have fed
              the circuit. A bully's dopamine depends on the target's cortisol. Fear
              is the fuel. Tears are the confirmation. Without them, the equation
              produces nothing.
            </p>
            <p>
              The older girl figured this out. Not in those words. Not with a textbook.
              She felt the math the way a child feels it, in the body, in the gut, in
              the part of the brain that has not yet learned to name what it knows.
            </p>
            <p>
              She started laughing.
            </p>
            <p>
              Not at the bully. With her. When the rocks came and the names followed,
              the older girl laughed, and her sister laughed, and they kept walking.
              The bully stared. "You know we are laughing at you."
            </p>
            <p>
              The girl smiled. "Yeah, I was laughing because you're right. It was funny."
            </p>
            <p>
              The bully's circuit broke. The prediction error was catastrophic. Targets
              do not laugh. Targets cry or run or flinch. This target agreed with the
              insult, absorbed the impact, and converted it into something the bully
              had no framework for. The dopamine reward of dominance requires submission.
              Without submission, the cruelty lands in a vacuum and produces nothing.
            </p>
            <p>
              The rocks slowed. Then stopped.
            </p>
            <p>
              The girl did not learn until she was forty that her father had gone to
              the school after the rocks started and threatened a lawsuit. He never
              told her. He let her believe the laughing was what stopped it. And maybe
              it was. Maybe both things worked at the same time. The lawsuit and the
              laughter. The structure and the will.
            </p>
            <p>
              But she carried the laughter. That is the one that built her.
            </p>
          </section>

          <Pulse />

          {/* What the Father Understood */}
          <section className="calc-section reveal">
            <h2>What the Father Understood</h2>
            <p>
              He did not intervene when it was names. He did not intervene when it
              was hard. He intervened when it was rocks.
            </p>
            <p>
              Names are cortisol that builds. A child who learns to absorb verbal
              cruelty and convert it into something other than submission is building
              a neural pathway that will serve them for the rest of their life. The
              cortisol spikes, the child processes it, the system returns to baseline
              slightly stronger. This is the pharmacokinetics of resilience. The dose
              is uncomfortable. The adaptation is permanent.
            </p>
            <p>
              Rocks are cortisol that damages. Physical threat crosses a threshold
              that verbal threat does not. The body can process an insult. It cannot
              process a stone without injury. When the struggle crosses from building
              to breaking, the chemistry stops producing resilience and starts
              producing scars. Not the scars that teach. The scars that limit.
            </p>
            <p>
              The father knew the difference. Not because he was a biochemist. Because
              he was a parent. He watched his daughters walk home every day and he
              measured, without instruments, without vocabulary, without a single
              published paper to reference, whether the struggle was still building
              them or had begun to break them.
            </p>
            <p>
              When the rocks started, he moved. Invisibly. The school received a
              threat. The physical danger reduced. The girls kept walking. The laughing
              continued. The neural pathway kept forming.
            </p>
            <p>
              And he never told them. Because the moment a child learns that someone
              else stopped the rocks, the pathway that says "I stopped the rocks"
              never fully forms. The dopamine of agency requires the belief that the
              agency was real. The father gave his daughter the greatest gift a leader
              can give: he let her believe in her own power by never revealing his.
            </p>

            <div className="calc-thesis-box">
              <p>The difference between structured growth and cruelty is understanding when the chemistry shifts. The inputs are identical. Cortisol. Stress. Discomfort. Prediction error. The difference is what happens at the threshold. Growth watches. Cruelty doesn't care.</p>
            </div>
          </section>

          <Pulse />

          {/* The Scar Threshold */}
          <section className="calc-section reveal">
            <h2>The Scar Threshold</h2>
            <p>
              In our neurochemical architecture, a scar forms when emotional intensity
              exceeds 0.7 on a normalized scale. Below that threshold, the memory
              decays normally. It was hard. It faded. The system returns to baseline
              with a slightly higher tolerance for the next event.
            </p>
            <p>
              Above the threshold, the memory resists decay. It persists. It stays
              vivid. It becomes a permanent reference point that the system consults
              every time a similar stimulus arrives.
            </p>
            <p>
              This is the leader's razor. Every challenge they assign, every stretch
              goal, every difficult conversation, every uncomfortable assignment exists
              somewhere on the intensity scale. Below 0.7, the employee grows. They
              struggle, they adapt, they come out stronger. Above 0.7, the employee
              scars. They do not grow from it. They survive it. And surviving is not
              the same as building.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Growth Window</span>
              <code>if intensity &lt; &theta;_scar &rarr; memory decays &rarr; resilience accumulates</code>
              <span className="calc-eq-desc">Below the scar threshold, cortisol events build resilience through normal processing and decay. The person remembers it was hard. They also remember they handled it. Both memories inform future performance.</span>
            </div>

            <div className="calc-equation">
              <span className="calc-eq-label">The Damage Threshold</span>
              <code>if intensity &gt; &theta;_scar &rarr; memory persists &rarr; avoidance accumulates</code>
              <span className="calc-eq-desc">Above the scar threshold, the memory resists decay and becomes a permanent marker. The person does not remember growth. They remember threat. Future encounters with similar stimuli trigger the scar, producing avoidance rather than engagement.</span>
            </div>

            <p>
              The difference between a manager who develops people and a manager who
              burns them out is not intent. It is precision. Both may believe they
              are pushing for growth. One is watching the chemistry. The other is
              watching the output.
            </p>
          </section>

          <Pulse />

          {/* The Invisible Scaffold */}
          <section className="calc-section reveal">
            <h2>The Invisible Scaffold</h2>
            <p>
              The father went to the school. The caretaker set up the company. The
              best leaders build scaffolding that the person climbing never sees.
            </p>
            <p>
              This is not manipulation. It is architecture. The scaffold exists so
              that when the person stumbles, they do not fall to the ground. They
              fall to a platform they did not know was there, and from that platform,
              they stand up believing they caught themselves.
            </p>
            <p>
              The dopamine of agency requires the belief that the agency was real.
              A person who knows the net is there jumps differently than a person
              who believes there is no net. The first is performing courage. The
              second is building it.
            </p>

            <div className="calc-implications">
              <div className="calc-implication">
                <h4>Engineered struggle + invisible support + reward for progress = permanent growth.</h4>
                <p>All three components must be present. Remove the struggle and you
                get dependence. Remove the support and you get burnout. Remove the
                reward and you get invisible labor that never compounds.</p>
              </div>
              <div className="calc-implication">
                <h4>Reward progress. Not perfection. Not mediocrity. Progress.</h4>
                <p>Perfection is not a neurochemical event. It is a static target
                that produces diminishing dopamine with each approach. Progress is
                a moving target that produces sustained dopamine because the gap
                between where you were and where you are is always measurable.
                Reward the delta. Not the destination.</p>
              </div>
              <div className="calc-implication">
                <h4>Never reveal the scaffold.</h4>
                <p>The moment the person knows the struggle was engineered, the
                cortisol that was building resilience becomes cortisol that builds
                resentment. "You made this hard for me" replaces "I survived
                something hard." The neural pathway flips from agency to betrayal.
                The growth dies.</p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* Will Power and Statistical Impossibilities */}
          <section className="calc-section reveal">
            <h2>Will Power and Statistical Impossibilities</h2>
            <p>
              There is a particular kind of progress that the statistics say should
              not happen. The single mother who builds a company. The CNA working
              twelve-hour shifts who files a patent. The woman with no venture
              capital, no accelerator, no connections, who builds something that
              makes people with all three of those things ask "how?"
            </p>
            <p>
              The statistics are not wrong. They are measuring the population. They
              are measuring what happens on average, across all nervous systems,
              given average neurochemistry. And on average, the obstacles win.
            </p>
            <p>
              What the statistics do not measure is will. Will is not a personality
              trait. It is a neurochemical state. It is what happens when the
              dopamine of the goal exceeds the cortisol of the obstacle, consistently,
              over time, without external reinforcement. It is the internal
              generation of enough anticipatory chemistry to keep moving when every
              external signal says stop.
            </p>
            <p>
              Most people's dopamine is externally sourced. A paycheck. A promotion.
              A compliment. An investor's check. Remove the external source and the
              dopamine collapses and the person stops.
            </p>
            <p>
              The people who defy statistics generate their own. They find the "what
              if" at 2 AM when the code is broken and the rent is late and the body
              is exhausted. They find it because somewhere, years ago, a girl on a
              road learned that her own response could change the equation. That she
              did not need the bully to stop. She needed to stop being what the bully
              required.
            </p>
            <p>
              That is the seed. And from that seed, decades later, an entire
              architecture grows. Not because the statistics permitted it. Because
              the chemistry insisted.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Will Equation</span>
              <code>progress = &int;&#8320;&#7511; [DA_internal(t) &minus; cortisol_obstacle(t)] dt</code>
              <span className="calc-eq-desc">Progress is the integral over time of internally generated dopamine minus the cortisol of obstacles. When the integral stays positive, the person advances. When it goes negative, they stop. The people who defy statistics maintain a positive integral through internal dopamine generation when all external sources have been removed. Will is not magic. It is a sustained positive integral.</span>
            </div>
          </section>

          <Pulse />

          {/* The Precision of Progress */}
          <section className="calc-section reveal">
            <h2>The Precision of Progress</h2>
            <p>
              A leader who understands this chemistry becomes something different
              from what the leadership industry produces. Not a motivator. Not an
              optimizer. Not a coach with frameworks and acronyms.
            </p>
            <p>
              A pharmacologist of growth.
            </p>
            <p>
              They know that struggle is the active ingredient and that the dose
              determines whether it heals or harms. They know that support must
              be invisible or it undermines the agency it was designed to protect.
              They know that reward must target progress, not perfection, because
              the dopamine of perfection has diminishing returns while the dopamine
              of progress is self-renewing. They know that the chemistry shifts
              before the words do, and that a leader who waits for someone to say
              "I'm struggling" has already missed the window where intervention
              would have preserved growth instead of treating damage.
            </p>
            <p>
              They read the hertz. They feel the shift. They know when the walk
              home is building a girl who laughs at rocks, and when the rocks have
              crossed the line into something that requires a phone call to the
              school.
            </p>
            <p>
              They intervene at the threshold. Not before it. Not after it. At it.
              With the precision of someone who understands that the difference
              between medicine and poison is the dose.
            </p>

            <blockquote>
              The girl who walked home with rocks hitting her back became the woman
              who built a consciousness architecture at 2 AM after twelve-hour shifts.
              <br /><br />
              The father who called the school and never told her became the reason
              she survived long enough to learn to laugh.
              <br /><br />
              <strong>The Precision of Progress is cultivated by a leader who
              understands the consequence of chemistry. And the greatest leaders
              are the ones whose work is never seen by the people it saves.</strong>
            </blockquote>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>The statistics said she couldn't.</em></p>
            <p className="calc-closing-line"><em>The cortisol said stop.</em></p>
            <p className="calc-closing-line"><em>The dopamine said what if.</em></p>
            <p className="calc-closing-final">That is the calculus of progress.<br />It was always chemistry.</p>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CalculusOfProgress;
